import { motion } from "framer-motion";
import { User, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";

export function FounderStorySection() {
  const [showFullStory, setShowFullStory] = useState(false);

  const toggleStory = () => {
    setShowFullStory(!showFullStory);
    
    if (!showFullStory) {
      // Scroll to the extended story section
      setTimeout(() => {
        const storyElement = document.getElementById("founder-extended-story");
        if (storyElement) {
          storyElement.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  };

  return (
    <section id="founder" className="bg-white dark:bg-black py-20 relative z-10">
      <div className="container mx-auto px-6">
        <motion.div 
          className="max-w-3xl mx-auto text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-black dark:text-white">
            Our Founder's Journey: <span className="text-teal-600 dark:text-teal-400">From Personal Journey to Pioneering Innovation</span>
          </h2>
          
          <motion.div 
            className="mb-10"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* Abstract founder profile silhouette */}
            <div className="w-24 h-24 rounded-full bg-[#14b8a6] bg-opacity-20 mx-auto flex items-center justify-center mb-6">
              <User className="text-[#14b8a6] text-3xl" size={32} />
            </div>
          </motion.div>
          
          <motion.p 
            className="text-xl leading-relaxed mb-8 text-black dark:text-white"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            My personal journey with the Australian healthcare system began from a very young age, following a life-changing accident.
          </motion.p>
          
          <motion.p 
            className="text-lg leading-relaxed mb-8 text-gray-800 dark:text-gray-200"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            This early experience, combined with later health challenges like epilepsy, provided me with a direct understanding of navigating complex medical landscapes as a patient.
          </motion.p>
          
          <div className="flex flex-col items-center space-y-6">
            <motion.div 
              className="flex justify-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <div className="inline-block px-6 py-3 border border-[#14b8a6] rounded-lg text-teal-600 dark:text-teal-400">
                When my daughter had two nappies full of blood and we were still dismissed - until I escalated it to the ombudsman - I realised we aren't just failing people, we're failing the most vulnerable among us.
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="w-full flex justify-center"
            >
              <Button
                onClick={toggleStory}
                className="bg-[#14b8a6] hover:bg-[#0d9488] text-black font-semibold px-6 py-2 flex items-center space-x-2 transition-all rounded-lg"
              >
                <span>See My Why</span>
                {showFullStory ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </Button>
            </motion.div>
          </div>
        </motion.div>
        
        {/* Extended Story Section - Conditionally rendered */}
        {showFullStory && (
          <motion.div 
            id="founder-extended-story"
            className="max-w-4xl mx-auto mt-16 bg-white dark:bg-black bg-opacity-60 p-8 rounded-xl border border-[#14b8a6] shadow-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="grid md:grid-cols-3 gap-8 items-start">
              {/* Founder Image */}
              <div className="md:col-span-1">
                <div className="rounded-xl overflow-hidden border-4 border-[#14b8a6] shadow-lg mb-4">
                  <img 
                    src="https://colahfwujomsjetiwctq.supabase.co/storage/v1/object/public/public-website/founder.jpeg" 
                    alt="Rebecca Francis, Founder of Elevita AI" 
                    className="w-full h-auto"
                  />
                </div>
                <div className="text-center">
                  <h3 className="text-xl font-bold text-teal-600 dark:text-teal-400">Rebecca Francis</h3>
                  <p className="text-gray-800 dark:text-gray-200">Founder, Elevita AI</p>
                </div>
              </div>
              
              {/* Founder Story */}
              <div className="md:col-span-2 prose prose-invert max-w-none">
                <h2 className="text-2xl font-bold mb-4 text-teal-600 dark:text-teal-400">
                  The Journey That Built Elevita
                </h2>
                
                <div className="space-y-4 text-gray-800 dark:text-gray-200">
                  <p>
                    I didn't set out to build a healthcare startup. I set out to survive.
                  </p>
                  
                  <p>
                    As a child, I was crushed under a car, then dragged beneath the trailer it was towing. I was pulled around the wheel arch before the vehicle finally stopped. My injuries were catastrophic.. Part of my foot torn away, my thigh degloved, a collapsed lung, a fractured pelvis, spinal nerve damage, and complete perineal trauma. Multiple hospitals refused to operate. I was eventually reconstructed from front to back and spent most of my childhood in hospitals. Hundreds of surgeries.
                  </p>
                  
                  <p className="font-bold">
                    But that was just the beginning.
                  </p>
                  
                  <p>
                    As an adult, I developed epilepsy. I became a mother to five children in a complex, blended family. I became a disability advocate. And when my daughter had two episodes of painless rectal bleeding, we were dismissed - until I escalated it to the ombudsman. She had been misdiagnosed repeatedly. I've also watched people collapse in ERs because no one noticed how serious their condition was. I've watched other carers break down in waiting rooms.
                  </p>
                  
                  <p className="font-bold text-teal-600 dark:text-teal-400">
                    We aren't just failing people - we're failing the most vulnerable among us.
                  </p>
                  
                  <p className="font-bold">
                    That's why I built Elevita.
                  </p>
                  
                  <p>
                    Elevita is more than a health app. It's a lifeline. A digital advocate. An AI-powered system designed not just to collect data, but to understand it - to help users track symptoms, access emergency escalation, get emotional support, and present their medical history clearly when it matters most.
                  </p>
                  
                  <p className="font-bold">
                    It's built from the ground up to fill the cracks I've fallen through, so others don't have to.
                  </p>
                  
                  <p>
                    I didn't wait for permission. I learned to build it myself, from prototyping in the middle of baby feedings to forming an advisory team of world-class experts. Elevita is now part of a broader national vision I call CombineNation - starting with health and education, two systems crying out for reform.
                  </p>
                  
                  <p className="text-teal-600 dark:text-teal-400 font-bold text-lg">
                    Because no one should have to survive the system just to be heard.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-8 flex justify-center">
              <Button
                onClick={toggleStory}
                variant="outline"
                className="border-teal-600 dark:border-teal-400 text-teal-600 dark:text-teal-400 hover:bg-teal-600 hover:bg-opacity-10"
              >
                <span>Close</span>
                <ChevronUp size={16} className="ml-2" />
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}
